import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertTriangle, ShieldCheck, Loader2, FileX, Info } from 'lucide-react';
import api from '../services/api';
import TradosPublicHeader from '../components/TradosPublicHeader';

const LOICreatedVerification = () => {
  const { loiId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchLOI = async () => {
      try {
        const res = await api.get(`/public/loi-creation/qr-validate/${loiId}`);
        if (res.data && res.data.found) {
          setData(res.data);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchLOI();
  }, [loiId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TradosPublicHeader />
        <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 56px)' }}>
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#2B4B6A] animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Validating LOI...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TradosPublicHeader />
        <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 56px)' }}>
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="bg-[#2B4B6A] px-8 py-7 text-center text-white">
              <div className="w-12 h-12 bg-white/15 rounded-xl mx-auto flex items-center justify-center mb-3">
                <span className="text-lg font-extrabold">T</span>
              </div>
              <h2 className="text-xl font-bold">Trados LOI Authentication</h2>
              <p className="text-sm opacity-80 mt-1">Letter of Intent Verification</p>
            </div>
            <div className="text-center py-10 px-8">
              <div className="w-16 h-16 rounded-full bg-red-50 border-2 border-red-200 mx-auto flex items-center justify-center mb-4">
                <FileX className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">LOI Not Found</h3>
              <p className="text-sm text-gray-500 mb-6">
                The LOI you are looking for does not exist or the link may be invalid.
              </p>
              <a
                href="/create-loi"
                className="inline-block px-6 py-3 bg-[#2B4B6A] text-white rounded-xl font-semibold hover:bg-[#3a6590] transition-colors"
              >
                Create an LOI on Trados
              </a>
            </div>
            <div className="text-center pb-6 text-xs text-gray-400">
              © 2026 Trados — The Trusted Trade Platform —{' '}
              <a href="https://www.trados.app" className="text-gray-400 hover:underline">
                www.trados.app
              </a>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  const detailRows = [
    { label: 'Buyer Name', value: data.buyer_name },
    { label: 'Email', value: data.buyer_email },
    { label: 'Company', value: data.buyer_company },
    { label: 'Commodity', value: data.commodity },
    { label: 'Quantity', value: data.quantity ? `${data.quantity}${data.quantity_unit ? ` ${data.quantity_unit}` : ''}` : null },
    { label: 'LOI Date', value: formatDate(data.created_at) },
    { label: 'Document ID', value: data.loi_number },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <TradosPublicHeader />
      <div className="flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-[#2B4B6A] px-8 py-7 text-center text-white">
            <div className="w-12 h-12 bg-white/15 rounded-xl mx-auto flex items-center justify-center mb-3">
              <span className="text-lg font-extrabold">T</span>
            </div>
            <h2 className="text-xl font-bold">Trados LOI Authentication</h2>
            <p className="text-sm opacity-80 mt-1">Letter of Intent Verification</p>
          </div>

          {/* Badge */}
          <div className="text-center py-5">
            <div className="w-16 h-16 rounded-full bg-green-50 border-[3px] border-green-200 mx-auto flex items-center justify-center mb-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <div className="text-lg font-bold text-green-600">LOI Created via Trados</div>
            <div className="text-sm text-gray-500 mt-1">Created on {formatDate(data.created_at)}</div>
          </div>

          {/* Details card */}
          <div className="px-8 pb-6">
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              {detailRows.map((row, idx) => (
                <div
                  key={row.label}
                  className={`flex items-center justify-between py-2.5 ${
                    idx !== detailRows.length - 1 ? 'border-b border-gray-200' : ''
                  }`}
                >
                  <span className="text-sm text-gray-500">{row.label}</span>
                  <span className="text-sm font-medium text-gray-800 text-right max-w-[60%] truncate">
                    {row.value || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Warning / Verified */}
          {!data.has_been_verified && (
            <div className="mx-8 mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>This LOI has NOT yet been verified by Trados.</strong>
                <br />
                While this document was created using the Trados platform, its contents and the parties
                involved have not been independently verified.
              </div>
            </div>
          )}

          {data.has_been_verified && (
            <div className="mx-8 mb-5 p-4 bg-green-50 border border-green-200 rounded-xl flex gap-3">
              <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <strong>This LOI has been verified by Trados.</strong>
                <br />
                The document contents have been independently analyzed and verified.
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="px-8 pb-5 space-y-3">
            <a
              href="/verify-loi"
              className="block w-full py-3.5 bg-[#2B4B6A] text-white text-center rounded-xl font-semibold hover:bg-[#3a6590] transition-colors"
            >
              Verify This LOI &rarr;
            </a>
            <a
              href="/register"
              className="block w-full py-3.5 border-2 border-[#2B4B6A] text-[#2B4B6A] text-center rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Create Your Free Account
            </a>
          </div>

          {/* Legal Disclaimer */}
          <div className="mx-8 mb-5 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <div className="flex gap-2 mb-2">
              <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Legal Disclaimer</span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Trados provides this document creation tool as a service only and assumes no ownership,
              responsibility, or liability for the content, accuracy, completeness, or legality of
              this Letter of Intent. All content within this LOI is solely the responsibility of the
              parties involved in its creation. Trados does not verify, endorse, or guarantee any of
              the information, terms, or representations contained herein.
            </p>
            <a
              href="/disclaimer"
              className="inline-block mt-2 text-xs text-[#2B4B6A] font-medium hover:underline"
            >
              Read Full Disclaimer &rarr;
            </a>
          </div>

          {/* Footer */}
          <div className="text-center pb-6 text-xs text-gray-400">
            © 2026 Trados — The Trusted Trade Platform —{' '}
            <a href="https://www.trados.app" className="text-gray-400 hover:underline">
              www.trados.app
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LOICreatedVerification;
